// src/pages/Caixa.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, getDocs, orderBy, addDoc, doc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import {
  ArrowDownCircle, ArrowUpCircle, DollarSign,
  PlusCircle, Download, Search, Trash2
} from 'lucide-react';
import { useEmpresaId } from "../context/EmpresaContext";
import MovimentacaoCaixaModal from '../components/MovimentacaoCaixaModal';

/* ===== Helpers ===== */
const formatBRL = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/* ===== Ícones tom-sobre-tom (padrão das tabelas) ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'warning':
      return 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/50';
    case 'neutral':
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};
const ActionIconBtn = ({ tone = 'neutral', title, children, className = '', ...props }) => (
  <button
    title={title}
    aria-label={title}
    className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold transition ${toneStyles(tone)} ${className}`}
    {...props}
  >
    {children}
  </button>
);

const SaldoCard = ({ titulo, valor, cor, icone }) => {
  const Icone = icone;
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg flex items-center gap-4">
      <div className={`p-3 rounded-full bg-opacity-10 ${cor.bg} ${cor.text}`}>
        <Icone size={28} />
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{titulo}</p>
        <p className={`text-2xl font-bold ${cor.text} whitespace-nowrap tabular-nums`}>
          {formatBRL(valor)}
        </p>
      </div>
    </div>
  );
};

export default function Caixa() {
  const empresaId = useEmpresaId();

  const [movimentacoes, setMovimentacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('mes_atual');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (empresaId) fetchMovimentacoes(empresaId);
    else setLoading(false);
  }, [empresaId]);

  const colCaixa = (id) => collection(db, 'empresas', id, 'caixa');

  const fetchMovimentacoes = async (empresaIdParam) => {
    setLoading(true);
    try {
      const q = query(colCaixa(empresaIdParam), orderBy('data', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          data: raw?.data?.toDate ? raw.data.toDate() : new Date()
        };
      });
      setMovimentacoes(data);
    } catch (error) {
      console.error('Erro ao buscar movimentações:', error);
    }
    setLoading(false);
  };

  const handleDeleteMovimentacao = async (movimentacaoId) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: 'Esta ação não pode ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (!empresaId || !result.isConfirmed) return;
      try {
        await deleteDoc(doc(db, 'empresas', empresaId, 'caixa', movimentacaoId));
        setMovimentacoes(prev => prev.filter(m => m.id !== movimentacaoId));
        Swal.fire('Excluído!', 'O lançamento foi removido com sucesso.', 'success');
      } catch (error) {
        console.error('Erro ao excluir movimentação: ', error);
        Swal.fire('Erro', 'Ocorreu um erro ao tentar excluir o lançamento.', 'error');
      }
    });
  };

  const dadosFiltrados = useMemo(() => {
    const hoje = new Date();
    let dataInicio;
    switch (filtroPeriodo) {
      case 'hoje': dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()); break;
      case 'semana': dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - hoje.getDay()); break;
      case 'ultimos_3_meses': dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 3, 1); break;
      case 'ano': dataInicio = new Date(hoje.getFullYear(), 0, 1); break;
      case 'mes_atual': default: dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1); break;
    }

    const movNoPeriodo = movimentacoes.filter(m => m.data >= dataInicio);
    const movAnteriores = movimentacoes.filter(m => m.data < dataInicio);

    const saldoAnterior = movAnteriores.reduce((acc, m) =>
      m.tipo?.toLowerCase() === 'entrada' ? acc + m.valor : acc - m.valor, 0);

    const entradas = movNoPeriodo.filter(m => m.tipo?.toLowerCase() === 'entrada')
      .reduce((acc, m) => acc + m.valor, 0);

    const saidas = movNoPeriodo.filter(m => m.tipo?.toLowerCase() === 'saida')
      .reduce((acc, m) => acc + m.valor, 0);

    const saldoPeriodo = entradas - saidas;
    const saldoAcumulado = saldoAnterior + saldoPeriodo;

    const movVisiveis = movNoPeriodo.filter(m =>
      (m.descricao || '').toLowerCase().includes(busca.toLowerCase()) ||
      (m.categoria || '').toLowerCase().includes(busca.toLowerCase())
    );

    return {
      movimentacoesVisiveis: movVisiveis,
      saldos: { entradas, saidas, saldoPeriodo, saldoAnterior, saldoAcumulado }
    };
  }, [movimentacoes, filtroPeriodo, busca]);

  const handleSaveMovimentacao = async (novaMovimentacao) => {
    if (!empresaId) return;
    try {
      await addDoc(colCaixa(empresaId), { ...novaMovimentacao });
      fetchMovimentacoes(empresaId);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar movimentação:', error);
    }
  };

  const exportToCSV = () => {
    const headers = ['Data', 'Tipo', 'Categoria', 'Descrição', 'Valor'];
    const rows = dadosFiltrados.movimentacoesVisiveis.map(m => [
      m.data.toLocaleDateString(),
      m.tipo,
      m.categoria,
      `"${(m.descricao || '').replace(/"/g, '""')}"`,
      (Number(m.valor) || 0).toFixed(2)
    ].join(','));
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'fluxo_de_caixa.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Fluxo de Caixa</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToCSV}
            className="bg-gray-200 dark:bg-gray-700 text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-300"
          >
            <Download size={16} /> Exportar CSV
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <PlusCircle size={16} /> Nova Movimentação
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <SaldoCard titulo="Entradas" valor={dadosFiltrados.saldos.entradas} cor={{ text: 'text-green-600', bg: 'bg-green-100' }} icone={ArrowUpCircle} />
        <SaldoCard titulo="Saídas" valor={dadosFiltrados.saldos.saidas} cor={{ text: 'text-red-600', bg: 'bg-red-100' }} icone={ArrowDownCircle} />
        <SaldoCard titulo="Saldo do Período" valor={dadosFiltrados.saldos.saldoPeriodo} cor={{ text: 'text-blue-600', bg: 'bg-blue-100' }} icone={DollarSign} />
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Saldo Anterior</p>
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap tabular-nums">
            {formatBRL(dadosFiltrados.saldos.saldoAnterior)}
          </p>
        </div>
        <div className="bg-gray-800 dark:bg-gray-900 text-white p-6 rounded-xl shadow-lg text-center">
          <p className="text-sm text-gray-300">Saldo Acumulado</p>
          <p className="text-2xl font-bold whitespace-nowrap tabular-nums">
            {formatBRL(dadosFiltrados.saldos.saldoAcumulado)}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg mb-6 flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-grow w-full sm:w-auto">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por descrição ou categoria..."
            className="border px-3 py-2 pl-10 rounded-md w-full bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <select
          value={filtroPeriodo}
          onChange={(e) => setFiltroPeriodo(e.target.value)}
          className="border px-3 py-2 rounded-md w-full sm:w-auto bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
        >
          <option value="hoje">Hoje</option>
          <option value="semana">Esta Semana</option>
          <option value="mes_atual">Mês Atual</option>
          <option value="ultimos_3_meses">Últimos 3 Meses</option>
          <option value="ano">Este Ano</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="sticky top-0 z-10 text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-6 py-3">Data</th>
              <th className="px-6 py-3">Tipo</th>
              <th className="px-6 py-3">Categoria</th>
              <th className="px-6 py-3">Descrição</th>
              <th className="px-6 py-3 text-right">Valor</th>
              <th className="px-6 py-3 text-center w-20">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="text-center p-6">A carregar...</td></tr>
            )}

            {!loading && dadosFiltrados.movimentacoesVisiveis.map(m => (
              <tr key={m.id} className="border-b dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-700/30">
                <td className="px-6 py-4 whitespace-nowrap">{m.data.toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      m.tipo?.toLowerCase() === 'entrada'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {m.tipo}
                  </span>
                </td>
                <td className="px-6 py-4">{m.categoria}</td>
                <td className="px-6 py-4 max-w-[680px] truncate">{m.descricao}</td>
                <td
                  className={`px-6 py-4 text-right font-bold tabular-nums whitespace-nowrap ${
                    m.tipo?.toLowerCase() === 'entrada' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatBRL(m.valor)}
                </td>
                <td className="px-6 py-4 text-center">
                  <ActionIconBtn
                    tone="danger"
                    title="Excluir lançamento"
                    onClick={() => handleDeleteMovimentacao(m.id)}
                  >
                    <Trash2 size={16} />
                  </ActionIconBtn>
                </td>
              </tr>
            ))}

            {!loading && dadosFiltrados.movimentacoesVisiveis.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center p-6 text-gray-500">
                  Nenhuma movimentação encontrada para este período.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <MovimentacaoCaixaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMovimentacao}
      />
    </div>
  );
}
